/**
 * CBD HUT — cbh-sticky-header.js
 * Extracted from header.liquid {% javascript %} to reduce inline workload.
 * Guarded so it won't double-define if theme already defines sticky-header.
 */

(() => {
  if (customElements.get('sticky-header')) return;

  class StickyHeader extends HTMLElement {
    constructor() {
      super();

      this.header = null;
      this.headerIsAlwaysSticky = false;
      this.headerBounds = { top: 0, bottom: 0 };

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.isScrolling = null;

      this._ticking = false;
      this._measureTicking = false;
      this._io = null;
      this.mql = null;

      this.onScrollHandler = this.onScrollHandler.bind(this);
      this.onResizeHandler = this.onResizeHandler.bind(this);
      this.hideHeaderOnScrollUp = this.hideHeaderOnScrollUp.bind(this);
      this.setHeaderHeight = this.setHeaderHeight.bind(this);
      this.scheduleMeasure = this.scheduleMeasure.bind(this);
    }

    connectedCallback() {
      this.header = this.closest('.section-header') || document.querySelector('.section-header');
      if (!this.header) return;

      const stickyType = this.getAttribute('data-sticky-type');
      this.headerIsAlwaysSticky = stickyType === 'always' || stickyType === 'reduce-logo-size';

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      // Measure once in rAF to avoid forced synchronous layout on connect.
      this.scheduleMeasure();

      this.mql = window.matchMedia('(max-width: 990px)');
      if (this.mql.addEventListener) {
        this.mql.addEventListener('change', this.scheduleMeasure);
      } else if (this.mql.addListener) {
        this.mql.addListener(this.scheduleMeasure);
      }

      window.addEventListener('resize', this.onResizeHandler, { passive: true });

      this.currentScrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      this.preventReveal = false;

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });

      this.createObserver();
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      window.removeEventListener('resize', this.onResizeHandler);

      if (this.mql) {
        if (this.mql.removeEventListener) {
          this.mql.removeEventListener('change', this.setHeaderHeight);
        } else if (this.mql.removeListener) {
          this.mql.removeListener(this.setHeaderHeight);
        }
      }

      if (this._io) {
        try { this._io.disconnect(); } catch (e) {}
        this._io = null;
      }

      window.clearTimeout(this.isScrolling);
    }

    onResizeHandler() {
      this.scheduleMeasure();
      this.createObserver();
    }

    scheduleMeasure() {
      if (this._measureTicking) return;
      this._measureTicking = true;
      requestAnimationFrame(() => {
        this._measureTicking = false;
        this.setHeaderHeight();
      });
    }

    setHeaderHeight() {
      if (!this.header) return;

      // Use offsetHeight for both height and bottom position. This avoids a
      // getBoundingClientRect() read (common forced reflow source) while still
      // being correct for a top-aligned header.
      const h = this.header.offsetHeight || 0;
      const bottom = Math.max(0, h);

      document.documentElement.style.setProperty('--header-height', `${h}px`);
      document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
      document.documentElement.style.setProperty('--cbh-header-bottom', `${bottom}px`);
    }

    createObserver() {
      if (!this.header || !('IntersectionObserver' in window)) {
        const h = this.header ? this.header.offsetHeight : 0;
        this.headerBounds = { top: 0, bottom: h };
        return;
      }

      if (this._io) {
        try { this._io.disconnect(); } catch (e) {}
      }

      this._io = new IntersectionObserver((entries, obs) => {
        if (entries && entries[0]) this.headerBounds = entries[0].intersectionRect;
        obs.disconnect();
      });

      this._io.observe(this.header);
    }

    onScrollHandler() {
      if (this._ticking) return;
      this._ticking = true;

      requestAnimationFrame(() => {
        this._ticking = false;
        this.onScroll();
      });
    }

    onScroll() {
      if (!this.header) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      const pastHeader = scrollTop > (this.headerBounds.bottom || 0);

      if (scrollTop > this.currentScrollTop && pastHeader) {
        this.header.classList.add('scrolled-past-header');
        this.hide();
      } else if (scrollTop < this.currentScrollTop && pastHeader) {
        this.header.classList.add('scrolled-past-header');

        if (!this.preventReveal) {
          this.reveal();
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = window.setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          this.hide();
        }
      } else if (scrollTop <= (this.headerBounds.top || 0)) {
        this.header.classList.remove('scrolled-past-header');
        this.reset();
      }

      this.currentScrollTop = scrollTop;
    }

    hideHeaderOnScrollUp() {
      this.preventReveal = true;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      const disclosures = this.header.querySelectorAll('header-menu');
      disclosures.forEach((disclosure) => disclosure.close());
    }
  }

  customElements.define('sticky-header', StickyHeader);
})();
